import type { ApiClient, FieldSubject } from '@carbonwebmail/api-client'
import { useCallback, useEffect, useState } from 'react'

/**
 * What this organisation calls the columns a board came with.
 *
 * Both boards have built-in columns whose names are code rather than data, and
 * both store an override per column somebody renamed. One hook over the two,
 * as `useDeclaredColumns` is for the columns an organisation creates — the
 * subject decides only which table the override lands in.
 *
 * **A failure leaves the board working under the default names.** That is the
 * board as it was before renaming existed: a worse board, not a broken one, and
 * taking a listing down because the labels did not answer would be the wrong
 * trade.
 *
 * **Renaming cannot change who a campaign reaches.** A saved rule and a merge
 * token name a field's *key*, and nothing here touches one.
 */
export function useColumnLabels({
  client,
  subject,
}: {
  readonly client: ApiClient
  readonly subject: FieldSubject
}) {
  const [labels, setLabels] = useState<Readonly<Record<string, string>>>({})

  const reload = useCallback(async () => {
    setLabels((await client.columnLabels(subject).catch(() => ({ labels: {} }))).labels)
  }, [client, subject])

  useEffect(() => {
    void reload()
  }, [reload])

  /** Answers the reason it was refused, or nothing. */
  const rename = useCallback(
    async (columnId: string, label: string): Promise<string | undefined> => {
      try {
        await client.renameColumn(subject, columnId, label)
        await reload()
        return undefined
      } catch {
        return 'We could not rename that column. Please try again in a moment.'
      }
    },
    [client, subject, reload],
  )

  return { labels, rename, reload }
}
